import React, { useEffect, useMemo, useState } from "react";
import { api, todayIso, addDays } from "@/lib/api";
import {
  X, Download, Upload, CalendarClock, FileUp, Check as CheckIcon,
  AlertCircle, RefreshCw, ArrowRight, CircleCheck, CircleAlert, Loader2,
  Bookmark, Trash2, Filter,
} from "lucide-react";

const firstOfMonth = () => todayIso().slice(0, 8) + "01";

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="kpi-label mb-1">{label}</div>
      {children}
    </label>
  );
}

function Chip({ kind }) {
  const map = {
    new: ["NEW", "hsl(var(--hud))"],
    exact: ["ALREADY EXISTS", "hsl(var(--muted-foreground))"],
    changed: ["CHANGED", "hsl(var(--oxblood))"],
  };
  const [txt, col] = map[kind] || ["—", "hsl(var(--muted-foreground))"];
  return (
    <span className="mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border"
      style={{ color: col, borderColor: col }}>{txt}</span>
  );
}

const fmtWhen = (e) => {
  if (!e?.date) return "—";
  if (e.all_day || !e.start_time) return `${e.date} · all-day`;
  return `${e.date} · ${e.start_time}${e.end_time ? "–" + e.end_time : ""}`;
};
const seriesTag = (e) =>
  e.series === "series_master" ? "Recurring series"
    : e.series === "occurrence" ? "Single occurrence" : null;

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join("; ");
  if (typeof d === "string") return d;
  return e?.message || "Something went wrong.";
};

export default function CalendarTransfer({ open, mode, googleReady, onClose, onChanged }) {
  const isImport = mode === "import";
  const [provider, setProvider] = useState("google");
  const [step, setStep] = useState("setup"); // setup | preview | summary
  const [from, setFrom] = useState(firstOfMonth());
  const [to, setTo] = useState(addDays(firstOfMonth(), 60));
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [events, setEvents] = useState([]);     // classified rows
  const [checks, setChecks] = useState({});      // uid -> bool (new rows)
  const [resolutions, setResolutions] = useState({}); // uid -> keep|update|both
  const [summary, setSummary] = useState(null);
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");
  const [keyword, setKeyword] = useState("");

  const reset = () => {
    setStep("setup"); setEvents([]); setChecks({}); setResolutions({});
    setSummary(null); setErr(null); setFile(null); setKeyword("");
  };
  const close = () => { reset(); onClose(); };

  // When the modal opens, default to .ics if Google isn't connected, and load presets.
  useEffect(() => {
    if (open) {
      setProvider(googleReady ? "google" : "ics");
      api.get("/calendar/presets").then((r) => setPresets(r.data || [])).catch(() => {});
    }
  }, [open, googleReady]);

  const applyPreset = (p) => {
    if (!p) return;
    setErr(null);
    setProvider(p.provider || "ics");
    if (p.date_from) setFrom(p.date_from);
    if (p.date_to) setTo(p.date_to);
    setKeyword(p.keyword || "");
  };

  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) { setErr("Name your preset first."); return; }
    try {
      const r = await api.post("/calendar/presets", {
        name, mode, provider, date_from: from, date_to: to, keyword,
      });
      setPresets((p) => [r.data, ...p]);
      setPresetName("");
    } catch (e) { setErr(errText(e)); }
  };

  const deletePreset = async (id) => {
    try {
      await api.delete(`/calendar/presets/${id}`);
      setPresets((p) => p.filter((x) => x.id !== id));
    } catch (e) { setErr(errText(e)); }
  };

  const modePresets = presets.filter((p) => p.mode === mode);

  const rowKey = (e) => e.uid || e.cc_id;

  const applyClassified = (rows) => {
    const ck = {}, rz = {};
    rows.forEach((e) => {
      const k = rowKey(e);
      if (e.match === "new") ck[k] = true;
      if (e.match === "changed") rz[k] = "keep"; // safe default; user must pick Update
    });
    setEvents(rows); setChecks(ck); setResolutions(rz); setStep("preview");
  };

  // ---- scan / parse -------------------------------------------------------
  const scan = async () => {
    setBusy(true); setErr(null);
    try {
      if (isImport && provider === "google") {
        const r = await api.get("/calendar/import/google", { params: { date_from: from, date_to: to } });
        applyClassified(r.data.events || []);
      } else if (isImport && provider === "ics") {
        if (!file) { setErr("Choose a .ics file first."); setBusy(false); return; }
        const fd = new FormData(); fd.append("file", file);
        const r = await api.post("/calendar/import/ics", fd, { timeout: 60000 });
        applyClassified(r.data.events || []);
      } else if (!isImport && provider === "google") {
        const r = await api.get("/calendar/export/preview", { params: { date_from: from, date_to: to }, timeout: 60000 });
        applyClassified(r.data.events || []);
      } else {
        // export ics — no dedup, just list CC events in range to pick
        const r = await api.get("/calendar-events");
        const rows = (r.data || [])
          .filter((e) => e.date && e.date >= from && e.date <= to)
          .map((e) => ({ ...e, uid: e.id, cc_id: e.id, match: "new", label: "Export" }));
        applyClassified(rows);
      }
    } catch (e) {
      setErr(errText(e) || "Something went wrong.");
    } finally { setBusy(false); }
  };

  // ---- commit -------------------------------------------------------------
  const buildImportItems = (rows) => rows.map((e) => {
    const k = rowKey(e);
    let action = "skip";
    if (e.match === "new") action = checks[k] ? "create" : "skip";
    else if (e.match === "changed") {
      const r = resolutions[k];
      action = r === "update" ? "update" : r === "both" ? "keep_both" : "skip";
    } else action = "skip"; // exact
    return {
      action,
      target_id: e.existing?.cc_id || null,
      provider_event_id: e.provider_event_id,
      title: e.title, all_day: e.all_day, date: e.date,
      start_time: e.start_time, end_time: e.end_time, timezone: e.timezone,
      location: e.location, description: e.description,
      recurrence: e.recurrence, series: e.series, recurring_event_id: e.recurring_event_id,
    };
  });

  const buildExportItems = (rows) => rows.map((e) => {
    const k = rowKey(e);
    let action = "skip";
    if (e.match === "new") action = checks[k] ? "create" : "skip";
    else if (e.match === "changed") {
      const r = resolutions[k];
      action = r === "update" ? "update" : r === "both" ? "keep_both" : "skip";
    } else action = "skip";
    return { id: e.cc_id, action, target_id: e.existing?.google_event_id || null };
  });

  const commit = async (rowsOverride) => {
    const rows = rowsOverride || filteredEvents;
    setBusy(true); setErr(null);
    try {
      if (isImport) {
        // Send every visible row (incl. skips) so created/updated/skipped counts are accurate.
        const all = buildImportItems(rows);
        if (all.filter((i) => i.action !== "skip").length === 0) {
          // Nothing to create/update. If rows exist only because they already
          // exist (or were kept), show that as a skipped summary, not an error.
          const autoSkipped = rows.filter((e) => e.match === "exact" || e.match === "changed").length;
          if (autoSkipped > 0) {
            setSummary({ created: 0, updated: 0, skipped: autoSkipped, failed: [] });
            setStep("summary"); onChanged?.(); setBusy(false); return;
          }
          setErr("Nothing selected to import."); setBusy(false); return;
        }
        const r = await api.post("/calendar/import/commit", { provider, items: all }, { timeout: 60000 });
        setSummary(r.data); setStep("summary"); onChanged?.();
      } else if (provider === "google") {
        const all = buildExportItems(rows);
        if (all.filter((i) => i.action !== "skip").length === 0) { setErr("Nothing selected to export."); setBusy(false); return; }
        const r = await api.post("/calendar/export/google", { items: all }, { timeout: 90000 });
        setSummary(r.data); setStep("summary"); onChanged?.();
      } else {
        // export ics download
        const ids = rows.filter((e) => checks[rowKey(e)]).map((e) => e.cc_id);
        if (ids.length === 0) { setErr("Select at least one event."); setBusy(false); return; }
        const r = await api.post("/calendar/export/ics", { ids }, { responseType: "blob", timeout: 60000 });
        const url = URL.createObjectURL(new Blob([r.data], { type: "text/calendar" }));
        const a = document.createElement("a");
        a.href = url; a.download = "command-center-events.ics"; a.click();
        URL.revokeObjectURL(url);
        setSummary({ created: ids.length, updated: 0, skipped: 0, failed: [], ics: true });
        setStep("summary"); onChanged?.();
      }
    } catch (e) {
      setErr(errText(e) || "Transfer failed.");
    } finally { setBusy(false); }
  };

  // retry: re-run only failed rows
  const retryFailed = async () => {
    const failedIds = new Set((summary?.failed || []).map((f) => f.id || f.uid));
    const rows = events.filter((e) => failedIds.has(e.cc_id) || failedIds.has(e.provider_event_id));
    if (rows.length === 0) return;
    await commit(rows);
  };

  // Only the events that pass the active title filter are ever shown,
  // selected, or committed — hidden rows must never transfer.
  const filteredEvents = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return kw ? events.filter((e) => (e.title || "").toLowerCase().includes(kw)) : events;
  }, [events, keyword]);

  const groups = useMemo(() => {
    const g = { new: [], changed: [], exact: [] };
    filteredEvents.forEach((e) => (g[e.match] || g.new).push(e));
    return g;
  }, [filteredEvents]);

  // Bulk conflict resolution — set every CHANGED row to one action, with a
  // confirm that names the affected count. Individual rows stay editable.
  const bulkResolve = (val) => {
    const changed = groups.changed;
    if (changed.length === 0) return;
    const label = { keep: "Keep existing", update: "Update existing", both: "Keep both" }[val];
    if (!window.confirm(`Apply “${label}” to ${changed.length} changed event${changed.length > 1 ? "s" : ""}? You can still adjust any row before confirming.`)) return;
    setErr(null);
    const rz = { ...resolutions };
    changed.forEach((e) => { rz[rowKey(e)] = val; });
    setResolutions(rz);
  };

  const newAllChecked = groups.new.length > 0 && groups.new.every((e) => checks[rowKey(e)]);
  const toggleAllNew = () => {
    setErr(null);
    const v = !newAllChecked; const ck = { ...checks };
    groups.new.forEach((e) => { ck[rowKey(e)] = v; });
    setChecks(ck);
  };

  const selectedCount =
    groups.new.filter((e) => checks[rowKey(e)]).length +
    groups.changed.filter((e) => ["update", "both"].includes(resolutions[rowKey(e)])).length;

  if (!open) return null;

  const isIcsExport = !isImport && provider === "ics";
  const needGoogle = provider === "google" && !googleReady;

  return (
    <div className="fixed inset-0 z-[90] flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm p-4"
      data-testid="calendar-transfer-modal">
      <div className="hud-frame w-full max-w-4xl my-8 bg-[hsl(var(--background))]">
        {/* header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-3">
            {isImport ? <Upload size={16} className="text-[hsl(var(--oxblood))]" />
              : <Download size={16} className="text-[hsl(var(--oxblood))]" />}
            <div>
              <div className="kpi-label">Calendar transfer</div>
              <div className="display text-lg uppercase tracking-tight leading-none">
                {isImport ? "Import events" : "Export events"}
              </div>
            </div>
          </div>
          <button onClick={close} data-testid="transfer-close" className="text-muted-foreground hover:text-foreground">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {err && (
            <div className="hud-frame p-3 flex items-start gap-2" style={{ borderColor: "hsl(var(--oxblood) / 0.6)" }} data-testid="transfer-error">
              <AlertCircle size={14} className="text-[hsl(var(--oxblood))] mt-0.5" />
              <div className="text-sm">{err}</div>
            </div>
          )}

          {/* ---- SETUP ---- */}
          {step === "setup" && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setProvider("google")}
                  data-testid="provider-google"
                  className={`hud-frame p-4 text-left transition-colors ${provider === "google" ? "ring-1 ring-[hsl(var(--oxblood))]" : ""}`}
                >
                  <div className="flex items-center gap-2"><CalendarClock size={15} /><span className="display text-sm uppercase">Google Calendar</span></div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {googleReady ? "Live two-way transfer with your Google calendar." : "Connect Google (with Calendar access) in Settings to enable."}
                  </p>
                </button>
                <button
                  onClick={() => setProvider("ics")}
                  data-testid="provider-ics"
                  className={`hud-frame p-4 text-left transition-colors ${provider === "ics" ? "ring-1 ring-[hsl(var(--oxblood))]" : ""}`}
                >
                  <div className="flex items-center gap-2"><FileUp size={15} /><span className="display text-sm uppercase">Apple / .ics file</span></div>
                  <p className="text-xs text-muted-foreground mt-1">
                    File transfer (not live sync). {isImport ? "Upload an .ics exported from Apple Calendar." : "Download an .ics to import into Apple Calendar."}
                  </p>
                </button>
              </div>

              {needGoogle && (
                <div className="text-xs text-[hsl(var(--oxblood))]">
                  Google isn’t connected for Calendar yet. Open Settings → connect Google with Calendar access, then come back.
                </div>
              )}

              {/* ---- saved presets ---- */}
              <div className="hud-frame p-3 space-y-2" data-testid="presets-panel">
                <div className="flex items-center gap-2 kpi-label">
                  <Bookmark size={12} /> Saved presets
                </div>
                {modePresets.length > 0 ? (
                  <div className="flex flex-wrap gap-2" data-testid="preset-list">
                    {modePresets.map((p) => (
                      <div key={p.id} className="flex items-center gap-1 border border-border rounded-full pl-3 pr-1 py-0.5">
                        <button onClick={() => applyPreset(p)} data-testid={`apply-preset-${p.id}`}
                          className="mono text-[11px] uppercase tracking-wider hover:text-[hsl(var(--oxblood))]">
                          {p.name}
                        </button>
                        <button onClick={() => deletePreset(p.id)} data-testid={`delete-preset-${p.id}`}
                          className="text-muted-foreground hover:text-[hsl(var(--oxblood))] p-1">
                          <Trash2 size={11} />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-[11px] text-muted-foreground">No presets yet — set up a transfer below and save it for next time. Applying a preset still lets you review every event before it transfers.</p>
                )}
                <div className="flex items-center gap-2 pt-1">
                  <input value={presetName} onChange={(e) => setPresetName(e.target.value)}
                    placeholder="Name this setup…" className="hud-input !py-1 flex-1" data-testid="preset-name" />
                  <button onClick={savePreset} className="hud-btn !py-1.5 flex items-center gap-1.5" data-testid="save-preset">
                    <Bookmark size={12} /> SAVE PRESET
                  </button>
                </div>
              </div>

              {/* date range for google (both) + ics export */}
              {(provider === "google" || isIcsExport) && (
                <div className="grid grid-cols-2 gap-4">
                  <Field label="From"><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="hud-input" data-testid="range-from" /></Field>
                  <Field label="To"><input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="hud-input" data-testid="range-to" /></Field>
                </div>
              )}

              {isImport && provider === "ics" && (
                <Field label="iCalendar file (.ics)">
                  <input type="file" accept=".ics,text/calendar" onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="hud-input" data-testid="ics-file" />
                </Field>
              )}

              <Field label="Title filter (optional)">
                <div className="relative">
                  <Filter size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input value={keyword} onChange={(e) => setKeyword(e.target.value)}
                    placeholder="Only show events whose title contains…"
                    className="hud-input !pl-8" data-testid="keyword-filter" />
                </div>
              </Field>

              {!isImport && provider === "google" && (
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <CircleAlert size={12} /> No attendee invitations or notifications are sent.
                </p>
              )}

              <div className="flex justify-end">
                <button onClick={scan} disabled={busy || needGoogle} className="hud-btn hud-btn-primary flex items-center gap-2" data-testid="transfer-scan">
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <ArrowRight size={13} />}
                  {isImport ? "SCAN & PREVIEW" : (isIcsExport ? "CHOOSE EVENTS" : "PREVIEW CHANGES")}
                </button>
              </div>
            </>
          )}

          {/* ---- PREVIEW ---- */}
          {step === "preview" && (
            <div className="space-y-5">
              {events.length === 0 && (
                <div className="text-center text-muted-foreground py-8 text-sm">No events found in this range/file.</div>
              )}

              {/* NEW / to-create group */}
              {groups.new.length > 0 && (
                <section data-testid="group-new">
                  <div className="flex items-center justify-between mb-2">
                    <div className="kpi-label">{isIcsExport ? `Events (${groups.new.length})` : `New (${groups.new.length})`}</div>
                    <button onClick={toggleAllNew} className="mono text-[11px] uppercase tracking-wider text-[hsl(var(--oxblood))]" data-testid="select-all-new">
                      {newAllChecked ? "Deselect all" : "Select all"}
                    </button>
                  </div>
                  <div className="hud-frame divide-y divide-border">
                    {groups.new.map((e) => {
                      const k = rowKey(e);
                      return (
                        <label key={k} className="flex items-center gap-3 px-3 py-2 cursor-pointer" data-testid={`row-new-${k}`}>
                          <input type="checkbox" checked={!!checks[k]} onChange={(ev) => { setErr(null); setChecks({ ...checks, [k]: ev.target.checked }); }} data-testid={`check-${k}`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm truncate">{e.title}</div>
                            <div className="mono text-[11px] text-muted-foreground">{fmtWhen(e)}{e.location ? ` · ${e.location}` : ""}{seriesTag(e) ? ` · ${seriesTag(e)}` : ""}</div>
                          </div>
                          {!isIcsExport && <Chip kind={e.match} />}
                        </label>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* CHANGED group — side by side, requires explicit choice to overwrite */}
              {groups.changed.length > 0 && (
                <section data-testid="group-changed">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <div className="kpi-label">Needs review — changed ({groups.changed.length})</div>
                    <div className="flex flex-wrap items-center gap-1.5" data-testid="bulk-resolve">
                      <span className="mono text-[10px] uppercase text-muted-foreground mr-1">Apply to all:</span>
                      {[["keep", "Keep existing"], ["update", "Update existing"], ["both", "Keep both"]].map(([val, lbl]) => (
                        <button key={val} onClick={() => bulkResolve(val)} data-testid={`bulk-${val}`}
                          className="mono text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full border border-border text-muted-foreground hover:border-[hsl(var(--oxblood))] hover:text-foreground transition-colors">
                          {lbl}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p className="text-[10px] text-muted-foreground mb-2">Exact duplicates are skipped automatically. Bulk actions ask for confirmation and you can still override any single row below.</p>
                  <div className="space-y-3">
                    {groups.changed.map((e) => {
                      const k = rowKey(e); const r = resolutions[k] || "keep";
                      return (
                        <div key={k} className="hud-frame p-3" data-testid={`row-changed-${k}`}>
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                              <div className="kpi-label">Existing</div>
                              <div className="text-sm truncate">{e.existing?.title}</div>
                              <div className="mono text-[11px] text-muted-foreground">{fmtWhen(e.existing)}{e.existing?.location ? ` · ${e.existing.location}` : ""}</div>
                            </div>
                            <div>
                              <div className="kpi-label text-[hsl(var(--oxblood))]">Incoming</div>
                              <div className="text-sm truncate">{e.title}</div>
                              <div className="mono text-[11px] text-muted-foreground">{fmtWhen(e)}{e.location ? ` · ${e.location}` : ""}</div>
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {[["keep", "Keep existing"], ["update", "Update existing"], ["both", "Keep both"]].map(([val, lbl]) => (
                              <button key={val} onClick={() => { setErr(null); setResolutions({ ...resolutions, [k]: val }); }}
                                data-testid={`resolve-${val}-${k}`}
                                className={`mono text-[11px] uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${r === val ? "bg-[hsl(var(--oxblood))] text-white border-[hsl(var(--oxblood))]" : "border-border text-muted-foreground hover:border-[hsl(var(--oxblood))]"}`}>
                                {lbl}{val === "update" ? " ★" : ""}
                              </button>
                            ))}
                          </div>
                          <p className="text-[10px] text-muted-foreground mt-2">Recommended: Update existing (same appointment). Choose explicitly — nothing is overwritten until you confirm.</p>
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* EXACT group */}
              {groups.exact.length > 0 && (
                <section data-testid="group-exact">
                  <div className="kpi-label mb-2">Already exists — skipped ({groups.exact.length})</div>
                  <div className="hud-frame divide-y divide-border opacity-70">
                    {groups.exact.map((e) => (
                      <div key={rowKey(e)} className="flex items-center gap-3 px-3 py-2" data-testid={`row-exact-${rowKey(e)}`}>
                        <CheckIcon size={13} className="text-muted-foreground" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate">{e.title}</div>
                          <div className="mono text-[11px] text-muted-foreground">{fmtWhen(e)}</div>
                        </div>
                        <Chip kind="exact" />
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <div className="flex items-center justify-between border-t border-border pt-4">
                <button onClick={() => setStep("setup")} className="hud-btn" data-testid="transfer-back">BACK</button>
                <div className="flex items-center gap-3">
                  <span className="mono text-[11px] text-muted-foreground">{selectedCount} selected</span>
                  <button onClick={() => commit()} disabled={busy} className="hud-btn hud-btn-primary flex items-center gap-2" data-testid="transfer-confirm">
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <ArrowRight size={13} />}
                    {isImport ? "IMPORT SELECTED" : (isIcsExport ? "DOWNLOAD .ICS" : "EXPORT SELECTED")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ---- SUMMARY ---- */}
          {step === "summary" && summary && (
            <div className="space-y-5" data-testid="transfer-summary">
              <div className="flex items-center gap-2">
                <CircleCheck size={18} className="text-[hsl(var(--hud))]" />
                <div className="display text-lg uppercase">{summary.ics ? "iCalendar file ready" : "Transfer complete"}</div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                {[["Created", summary.created], ["Updated", summary.updated], ["Skipped", summary.skipped], ["Failed", summary.failed_count ?? (summary.failed?.length || 0)]].map(([l, v]) => (
                  <div key={l} className="hud-frame p-3 text-center">
                    <div className="display text-2xl">{v ?? 0}</div>
                    <div className="kpi-label">{l}</div>
                  </div>
                ))}
              </div>
              {summary.ics && <p className="text-xs text-muted-foreground">Open the downloaded file in Apple Calendar to add these events. This is a file transfer, not a live sync.</p>}
              {(summary.failed?.length || 0) > 0 && (
                <div className="hud-frame p-3 space-y-2" style={{ borderColor: "hsl(var(--oxblood) / 0.6)" }}>
                  <div className="kpi-label text-[hsl(var(--oxblood))]">Failed</div>
                  {summary.failed.map((f, i) => (
                    <div key={i} className="text-xs flex justify-between gap-2">
                      <span className="truncate">{f.title || f.id || f.uid}</span>
                      <span className="text-muted-foreground">{f.error}</span>
                    </div>
                  ))}
                  <button onClick={retryFailed} disabled={busy} className="hud-btn flex items-center gap-2 mt-1" data-testid="retry-failed">
                    {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={12} />} RETRY FAILED
                  </button>
                </div>
              )}
              <div className="flex justify-end">
                <button onClick={close} className="hud-btn hud-btn-primary" data-testid="transfer-done">DONE</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
